import {Component, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy} from "@angular/core";
import {NavigationEnd, Router} from "@angular/router";
import {filter} from "rxjs/operators";
import {Monitor} from "./services/monitor.service";
import {AuthService} from "./services/auth.service";
import {PerformaModulesService} from "./services/performa-modules.service";
import {PerformaInfoService} from "./services/performa-info.service";
import {SafeTooltipTemplateService} from "./services/safe-tooltip-template.service";
import {ModuleDto, PerFormaAPIService} from "./services/pfapi.service";
import {lastValueFrom} from "rxjs";

interface NavItem {
    label: string;
    link: string;
}

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {
    username = "";
    showShell = true;
    // minimal header (blue bar + logo, no menu/user actions) for public account pages other than
    // login — mirrors the legacy _Layout HideMenu look. login stays bare (full-screen background).
    showAuthHeader = false;
    // true when running inside an iframe — the ptp forms popup hosts its form routes in an
    // <iframe>, and embedded pages render chromeless (no site shell).
    private readonly inIframe = window.self !== window.top;
    year = new Date().getFullYear();

    // precomputed nav state — never derive these from the template (runs every change detection)
    navItems: NavItem[] = [];
    showReports = false;
    reportsLink = "/report/arreport";
    canConfigure = false;

    supportUrl = "";
    version = "";

    // password-expiry warning banner (mirrors legacy Site.Master: shown within 14 days of expiry)
    showPasswordWarning = false;
    passwordWarningDays = 0;

    // shared content template for the [safeTooltip] directive (HTML rendered into the ngb tooltip)
    @ViewChild("safeTooltipContent", {static: true}) private safeTooltipContent!: TemplateRef<{$implicit: string}>;

    constructor(
        private readonly router: Router,
        private readonly authService: AuthService,
        private readonly modulesService: PerformaModulesService,
        private readonly infoService: PerformaInfoService,
        private readonly tooltipTemplates: SafeTooltipTemplateService,
        private readonly api: PerFormaAPIService,
        // injecting Monitor sends a pageView to AppInsights for every navigation
        private readonly monitor: Monitor
    ) {}

    async ngOnInit(): Promise<void> {
        this.tooltipTemplates.content = this.safeTooltipContent;
        this.supportUrl = (this.infoService.supportInfo && this.infoService.supportInfo.url) || "";

        this.updateShellVisibility(this.router.url);
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(event => this.updateShellVisibility((event as NavigationEnd).urlAfterRedirects));
        this.authService.sessionChanged$.subscribe(() => void this.refreshShellState());

        await this.refreshShellState();
    }

    // (re)computes all user-dependent shell state from the current session. safe to call repeatedly.
    private async refreshShellState(): Promise<void> {
        this.username = this.authService.username;
        this.canConfigure = this.authService.isAdmin;
        this.updatePasswordWarning();
        await this.buildNav();
        // version is auth-gated, so it's blank until a session exists — refetch on every session
        // change rather than once at bootstrap so a fresh login's shell shows the version.
        this.version = (await lastValueFrom(this.api.getApiSystemVersion())) || "";
    }

    private updatePasswordWarning(): void {
        this.showPasswordWarning = false;
        this.passwordWarningDays = 0;

        const expiresOn = this.authService.passwordExpiresOn;
        if (!expiresOn) return;

        const msPerDay = 1000 * 60 * 60 * 24;
        const days = Math.floor((expiresOn.getTime() - Date.now()) / msPerDay);
        if (days >= 0 && days <= 14) {
            this.showPasswordWarning = true;
            this.passwordWarningDays = days;
        }
    }

    logout(): void {
        if (confirm("Are you sure you want to logout?")) {
            localStorage.removeItem("pf_token");
            localStorage.removeItem("pf_refresh");
            this.router.navigateByUrl("/account/login");
        }
    }

    private updateShellVisibility(url: string): void {
        const path = (url || "").toLowerCase();
        const isAccount = path.startsWith("/account/");
        const isLogin = path.startsWith("/account/login");
        // ptp edit forms render chromeless (their own blank page) — mirrors the legacy forms
        // layout that hid the site header. covers the ptp forms popup and its form sub-routes
        // (/ptp/forms, /ptp/po, /ptp/porequest, ...) plus anything hosted in an iframe (e.g. the
        // Attach Invoice tab's /upload?ptp=true). the main /ptp page keeps the full shell.
        const isBareForm = path.startsWith("/ptp/") || this.inIframe;
        // hide the full shell on all public auth pages (login, forgot-password, reset-password)
        this.showShell = !isAccount && !isBareForm;
        // bare form pages render on a plain white page (legacy forms layout), not the app grey
        document.body.classList.toggle("pf-embedded", isBareForm);
        // login is full-screen with its own background; the other account pages keep the blue
        // header bar + logo (menu/user actions hidden) like the legacy HideMenu layout
        this.showAuthHeader = isAccount && !isLogin;
    }

    private async buildNav(): Promise<void> {
        const modules = ((await this.modulesService.Modules()) || []) as ModuleDto[];
        const enabled = new Set(modules.filter(m => m.IsEnabled).map(m => m.Name));

        const items: NavItem[] = [{label: "Search", link: "/search"}];
        // Upload is gated on the fine-grained per-doctype UploadFile right (legacy _Layout behavior)
        if (this.authService.canUpload) items.push({label: "Upload", link: "/upload"});
        if (enabled.has("Scan")) items.push({label: "Scan", link: "/scan"});
        if (enabled.has("PTP")) items.push({label: "Purchase To Pay", link: "/ptp"});
        if (enabled.has("APCheck")) items.push({label: "AP-Check", link: "/ap"});

        this.showReports = enabled.has("ARReport") || enabled.has("FINReport");
        this.reportsLink = enabled.has("ARReport") ? "/report/arreport" : "/report/finreport";
        if (this.showReports) items.push({label: "Reports", link: this.reportsLink});

        // Settings link is admin-only (PerForma admin or scan admin), replacing the always-visible default
        this.canConfigure = this.authService.isAdmin || this.authService.isScanAdmin;

        this.navItems = items;
    }
}
